/* eslint-disable react-func/max-lines-per-function */
export type CrumbRoute =
  | {
      path: string; // Puede incluir segmentos dinámicos, ej: '/:websiteId'
      label: string | ((...params: string[]) => string);
      children?: CrumbRoute[];
      slot?: never;
    }
  | {
      path: string;
      slot: (...params: string[]) => { label: string; id: string };
      children?: CrumbRoute[];
      label?: never;
    };

export interface BreadcrumbItem {
  label: string;
  path: string;
}

/**
 * Función para unir paths de forma "inteligente"
 * Si existe un padre, se concatena respetando las barras.
 */
function joinPaths(parent: string, child: string): string {
  // Si el child es absoluto y hay padre, lo tratamos como relativo
  if (parent !== '/' && child.startsWith('/')) {
    child = child.slice(1);
  }
  // Si el padre es la raíz, cuidamos la barra
  if (parent === '/') {
    return child.startsWith('/') ? child : '/' + child;
  }
  return parent + (child.startsWith('/') ? child : '/' + child);
}

/**
 * Intenta hacer match de un patrón con la URL actual.
 * Separa ambos en segmentos y si el patrón es menor o igual en cantidad,
 * compara cada segmento: si es dinámico (empieza con ':') lo extrae,
 * si es estático, debe coincidir exactamente.
 */
function matchPath(
  pattern: string,
  location: string
): { params: Record<string, string>; matchedSegments: number } | null {
  const patternSegments = pattern.split('/').filter(Boolean);
  const locationSegments = location.split('/').filter(Boolean);

  if (patternSegments.length > locationSegments.length) {
    return null;
  }
  const params: Record<string, string> = {};

  for (let i = 0; i < patternSegments.length; i++) {
    const pSeg = patternSegments[i];
    const lSeg = locationSegments[i];

    if (pSeg.startsWith(':')) {
      params[pSeg.slice(1)] = lSeg;
    } else if (pSeg !== lSeg) {
      return null;
    }
  }
  return { params, matchedSegments: patternSegments.length };
}

/**
 * Recorre recursivamente el árbol de rutas para encontrar la cadena
 * que coincida (en la mayor profundidad posible) con la URL actual.
 */
function findChain(
  routes: CrumbRoute[],
  location: string,
  parentPath = ''
): Array<{
  route: CrumbRoute;
  effectivePath: string;
  params: Record<string, string>;
}> {
  let bestChain: Array<{
    route: CrumbRoute;
    effectivePath: string;
    params: Record<string, string>;
  }> = [];

  for (const route of routes) {
    const effectivePath = parentPath
      ? joinPaths(parentPath, route.path)
      : route.path;
    const match = matchPath(effectivePath, location);

    if (match) {
      // Creamos la cadena actual con el nodo encontrado
      const currentChain = [{ route, effectivePath, params: match.params }];
      // Si existen rutas hijas, intentamos profundizar
      let childChain: Array<{
        route: CrumbRoute;
        effectivePath: string;
        params: Record<string, string>;
      }> = [];

      if (route.children) {
        childChain = findChain(route.children, location, effectivePath);
      }
      const fullChain = currentChain.concat(childChain);

      // Seleccionamos la cadena que haya coincidido más profundamente
      if (fullChain.length > bestChain.length) {
        bestChain = fullChain;
      }
    }
  }
  return bestChain;
}

/**
 * Función que a partir de la configuración de rutas retorna un objeto
 * con el método get que, dada una URL, devuelve un array de BreadcrumbItem.
 */
export function createCrumbs(routes: CrumbRoute[]): {
  get: (location: string) => BreadcrumbItem[];
} {
  return {
    get: (location: string): BreadcrumbItem[] => {
      const chain = findChain(routes, location);
      let accumulatedPath = '';
      const crumbs: BreadcrumbItem[] = [];

      chain.forEach((item) => {
        const route = item.route;
        let segment = route.path;

        if (accumulatedPath && segment.startsWith('/')) {
          segment = segment.slice(1);
        }
        let label: string;

        if (typeof route.slot === 'function') {
          const paramMatches = Array.from(segment.matchAll(/:([^/]+)/g));
          const paramValues = paramMatches.map((m) => {
            const paramName = m[1];

            return item.params[paramName];
          });
          const slotResult = route.slot(...paramValues);

          segment = segment.replace(/:[^/]+/g, slotResult.id);
          label = slotResult.label;
        } else if (typeof route.label === 'function') {
          const paramMatches = Array.from(segment.matchAll(/:([^/]+)/g));
          const paramValues = paramMatches.map((m) => {
            const paramName = m[1];

            return item.params[paramName];
          });

          label = route.label(...paramValues);
          segment = segment.replace(
            /:([^/]+)/g,
            (_, p1: string) => item.params[p1] || `:${p1}`
          );
        } else {
          label = route.label;
          segment = segment.replace(
            /:([^/]+)/g,
            (_, p1: string) => item.params[p1] || `:${p1}`
          );
        }
        if (!accumulatedPath) {
          accumulatedPath = segment.startsWith('/') ? segment : '/' + segment;
        } else {
          accumulatedPath = joinPaths(accumulatedPath, segment);
        }
        crumbs.push({ label, path: accumulatedPath });
      });
      return crumbs;
    },
  };
}
